/**
 * Der Reiter „Zahlung" des Bestell-Drawers - Portierung von `drawerPayTab`
 * (`index.html:8626-8722`) samt der Renderer und Handler, die der Bestand daneben laufen laesst:
 * `renderPayments`/`renderPaySum` (`:9325-9419`, ausgelagert nach `Ratenplan.tsx`),
 * `setPaymentMode` (`:9421`), `patchPayment` (`:9441`), `applyTermsToPayments` (`:9511`) und
 * `initiatePayment` (`:9880`, ausgelagert nach `Freigabe.tsx`).
 *
 * Aufbau (Reihenfolge des Originals `drawerPayTab`, ohne die drei letzten Abschnitte):
 *
 *   sec('pay-action',        'Zahlung anstoßen')       Freigabe.tsx
 *   sec('pay-plan',          'Zahlstatus')             VorlagenAuswahl.tsx + Ratenplan.tsx
 *   sec('pay-geloest',       'Gelöste Zahlungsbelege') GeloesteBelege.tsx (neu, nur mit Einträgen, siehe unten)
 *
 * ================= Ticket H (Asana „Reiter Zahlung aufräumen", 2026-10-20): drei Abschnitte entfernt ======
 *
 * `sec('pay-fx', 'Kurssicherung ($)')`, `sec('pay-course-credit', 'Kurswechsel & Guthaben')` und
 * `sec('pay-beleg', 'Überweisungsbelege')` sind ersatzlos entfernt - samt ihrer Handler
 * (`toggleFxNone`, `renderFx`/`renderFxSum`, `patchFxAlloc`/`bindFxToOpen`/`unbindFxFromOpen`, `patchPaid`,
 * `renderPaymentReceiptGroups`, `paymentMailDraft`/`openPaymentMail`) und der Props `deals`, `onZuordnung`,
 * `onHochladen`, `onLoeschen`, `onMail`. Gemessen in Produktion (2193 Bestellungen): Guthaben nie gepflegt (0),
 * Bezahlt-Haken bei Bestellungen ohne Raten 0, „Vorbereitete Mail öffnen" 2x insgesamt. Wo es stattdessen
 * weitergeht: Sicherungen binden und loesen auf der Seite „Zahlungen & Kurssicherung"
 * (`PaymentsPage/SicherungDetail.tsx`), Belege an bezahlten Raten im Zahlstatus (`Ratenplan.tsx`/`IstZahlung.tsx`),
 * Mischkurs und Kurs im EK-Reiter. KEINE Datenaenderung: die Spalten (`supplierCredit`, `creditCurrency`, `fxNone`,
 * `paymentMailPreparedAt`/`…By`) bleiben in der Datenbank, nur die Oberflaeche dafuer entfaellt.
 *
 * Nachtrag zu Ticket H (Review W1, vom Nutzer freigegeben): der entfernte Abschnitt „Überweisungsbelege“ war der einzige
 * Ort, an dem Anzahlungs-/Restzahlungsbelege ohne Ist-Zahlung und gelöste Belege (nach Rücknahme oder Reset) herunterladbar
 * waren. Dafür steht unter dem Zahlstatus die Liste „Gelöste Zahlungsbelege“ (`GeloesteBelege.tsx`): nur Download, nur mit
 * Einträgen. Die Sicht-Bedingung und die Quellen stehen im Kopfkommentar von `GeloesteBelege.tsx`.
 *
 * ================= RECHTE: dieser Reiter ist der EINZIGE, der selbst eines prueft ===============
 *
 * Anders als die Reiter „Bestellung" (Task 17), „Tasks" (Task 18) und „Dokumente" (Task 19) traegt
 * dieser Reiter im Bestand zwei Rechtestellen:
 *
 * 1. `data-permission="request_payment"` am Anstoss-Knopf (`index.html:8632`) - die EINZIGE
 *    Vorkommnis dieses Attributs im gesamten `index.html`. Ausgewertet wird es nicht hier, sondern
 *    im Rechte-Durchgang des Rahmens (`Drawer.tsx`, `index.html:9296-9304`).
 * 2. `if (!hasPermission('request_payment')) return toast(…)` in `initiatePayment` selbst
 *    (`index.html:9883`) - eine zweite, vom Sperren unabhaengige Pruefung im Klickhandler.
 *
 * Nummer 1 ist in der Richtung, fuer die sie gedacht war, WIRKUNGSLOS - siehe `Freigabe.tsx`,
 * Abschnitt „data-permission". Beides woertlich portiert und in `zahlungTab.test.tsx` in beide
 * Richtungen festgenagelt. Gemeldet, nicht repariert.
 *
 * ================= Die Normalisierung: im Bestand beim LADEN, hier fuer die ANZEIGE =============
 *
 * `renderPayments` verlaesst sich darauf, dass `o.payments` schon normalisiert ist: der Bestand ruft
 * `normalizeOrderPayments(o)` einmal pro Bestellung in der Datenwanderung (`index.html:5848`,
 * `migrateData`) und schreibt das Ergebnis zurueck. Eine Bestellung ohne Raten hat im Drawer
 * deshalb IMMER mindestens eine Zeile.
 *
 * `migrateData` ist nicht nach `lib/` portiert (siehe die Bekannte-Luecken-Tabelle des Branch-Kopfes
 * und Befund 11) - dieser Reiter kann sich also nicht darauf verlassen, dass sein Aufrufer
 * normalisiert hat. Er normalisiert deshalb selbst, aber auf einer KOPIE
 * (`bestellungFuerAnzeige`): `normalizeOrderPayments` VERAENDERT die uebergebene Bestellung
 * (`paymentMode`, `payments`, `paid`), und eine Komponente darf ihre Requisiten nicht anfassen.
 * Sichtbar ist das Ergebnis dasselbe wie im Bestand nach der Wanderung; der Unterschied ist, dass
 * hier nichts zurueckgeschrieben wird (das waere Sache eines Store-/Wanderungs-Tasks).
 *
 * NACHBESSERUNG (Task 6, Zahlungsraten-Reparatur): `normalizeOrderPayments` leitet `paid` bei
 * JEDEM Aufruf aus dem Zeilenstatus ab (`every(status === "paid")`) - fuer die reine ANZEIGE gilt
 * das nicht mehr. `bestellungFuerAnzeige` stellt das rohe `paid`-Flag der Bestellung NACH dem
 * Normalisieren wieder her, weil das Kopf-Flag die verbindliche Quelle fuer "Bestellung bezahlt"
 * ist - ohne diese Wiederherstellung waere ein gespeichertes `paid: true` beim naechsten Rendern
 * sofort wieder verschwunden, solange nicht ALLE Raten auf "paid" stehen. (Der Haken, der das Flag
 * von Hand setzte, entfiel mit Ticket H; ausgewertet wird das Flag nur noch in `hatZahlstatus` unten, fuer den
 * Zahlstatus-Reset.)
 *
 * Genau an der Normalisierung haengt weiterhin Befund 6 (Kernbefund unveraendert): eine Bestellung
 * mit `payments: []` und `paid: true` zeigt eine OFFENE Gesamtzahlung - die einzelne Ratenzeile
 * bleibt "offen", weil ihr Status nirgends aus dem Kopf-Flag nachgezogen wird (toter
 * `: !!o.paid`-Zweig, `lib/payments.ts`, Eigenheit 3, betrifft nur den Fall `payments.length === 0`
 * vor der Normalisierung und damit nie den hier gepflegten Wiederherstellungspfad). Die zugespitzte
 * ZWEITE Haelfte des urspruenglichen Befunds - Kopfzeile UND Haken zeigten trotz `paid: true`
 * "unbezahlt" - war mit der Wiederherstellung oben behoben; Kopfzeile und Haken gibt es seit Ticket H
 * nicht mehr. Woertlich portiert (Rate bleibt offen), per Test festgenagelt.
 *
 * ================= Eine Naht-Stelle ohne Gegenstueck in diesem Baum ==============================
 *
 * **`toast(…)`.** Der Bestand meldet Ausgaenge dieses Reiters per Hinweisband (`Zahlungsart: …`,
 * `N Rate(n) aus dem Zahlungsziel erzeugt`, `Aus dem Zahlungsziel ließen sich keine Raten ableiten …`,
 * `Du darfst keine Zahlung anstoßen`). Ein solches Band gibt es in der SPA noch nicht (im ganzen
 * `frontend/src` kommt `toast` nicht vor). `Du darfst keine Zahlung anstoßen` meldet `Freigabe.tsx`
 * seit Fehleranzeige Paket 1 (Task 8) im Wortlaut des Bestands selbst am Fehlerstapel; die uebrigen
 * entfallen, das Verhalten daneben bleibt. (Die Hinweise zu „Sicherung binden" - `Maximal $ N verfügbar`,
 * `Betrag eingeben`, `Auf freien Rest gekappt: $ N` - entfielen mit dem Abschnitt Kurssicherung.)
 *
 * ================= Befund 5 (`orderTotal`/`payAmount` wirft bei fehlendem Datensatz) ============
 *
 * Task 3 (Verdrahtung, Entscheidungsvorlage `2026-08-09-auffaelligkeiten-entscheidungsvorlage.md`,
 * "## Befund 5") haelt Option C fest: `orderTotal`/`payAmount`/`calcEk` duerfen nur mit einer
 * TATSAECHLICH GELADENEN Bestellung gerufen werden, und die "Trifft in der SPA"-Liste dieses
 * Befunds nennt `Drawer/PaymentTab` bereits ausdruecklich (`registratur.ts`, Zeile 68 - dort schon
 * eingetragen, dieser Task aendert daran nichts, siehe Task-Bericht: "kein zweiter
 * Vorlagen-Nachzug"). `BoardSeite`/`KostenSeite` brauchten dafuer einen eigenen Guard, weil sie
 * `orderTotal`/`calcEk` auf einen per id AUS EINER LISTE NACHGESCHLAGENEN Datensatz anwenden
 * (`bestellungen.find((b) => b.id === id)`), der zwischen Ziehen/Klick und Zeichnen aus der Liste
 * gefallen sein kann.
 *
 * Dieser Reiter braucht KEINEN zweiten, eigenen Guard: `bestellung` (unten, `payAmount` via
 * `Ratenplan.tsx`) ist kein Nachschlagewert, sondern die unmittelbare, PFLICHTIGE Prop dieser
 * Komponente. Ihr einziger Aufrufer, `components/App/App.tsx`, zeichnet `<Drawer>` (und damit jeden
 * Reiter-Inhalt) ausschliesslich hinter `drawer && offeneBestellung` (`offeneBestellung =
 * zustand.bestellungen.find(...)`, Task 4) - der Fall "Datensatz (noch) nicht geladen" ist an DIESER
 * Stelle bereits abgefangen, bevor `ZahlungTab` ueberhaupt gezeichnet wird. Ein zweites `if (!o)
 * return` hier haette keinen erreichbaren Eingang.
 */
import { Freigabe } from "./Freigabe.tsx";
import { VorlagenAuswahl } from "./VorlagenAuswahl.tsx";
import { GeloesteBelege } from "./GeloesteBelege.tsx";
import { PAYMENT_STATUSES } from "../../../config/payments.ts";
import { ensurePaymentRequestTask, paymentStatusOf } from "../../../lib/payments.ts";
import type { IstZahlungAktionen, IstZahlungHandler } from "./IstZahlung.tsx";
import type { IstZahlung as IstZahlungTyp } from "../../../lib/types.ts";
import { ratenFuerAnzeige } from "../../../lib/zahlungsplanEditor.ts";
import type { PlanRateEingabe } from "../../../lib/zahlungsplanEditor.ts";
import type { PaymentTemplateRow } from "../../../api/zahlungsvorlagen.ts";
import type { ZahlungszielRaten } from "../../../api/bestellungZahlungsziel.ts";
import type { FreigabeZeile } from "../../../api/lieferantenZahlungsziele.ts";
import type {
  Order,
  OrderDocument,
  OrderPayment,
  OrderTask,
  ResponsibilityModule,
  Supplier,
} from "../../../lib/types.ts";

/**
 * Die Bestellung, wie `renderPayments` sie im Bestand vorfindet - siehe Kopfkommentar,
 * „Die Normalisierung". Die Raten werden Zeile fuer Zeile kopiert, weil `normalizePaymentRow` sie an
 * Ort und Stelle veraendert.
 */
function bestellungFuerAnzeige(o: Order): Order {
  const rohesPaid = o.paid;
  const rohe = o.payments;
  const kopie = {
    ...o,
    payments: Array.isArray(rohe)
      ? (rohe as OrderPayment[]).map((p) => {
          const zeile = { ...p } as OrderPayment;
          // Befund 1 der Gesamtpruefung (2026-09-01): eine vom Server geladene Zeile traegt
          // `dueDate`, nicht `due` (siehe `zahlungssynchronisation.ts#ratenFelderAusZeile`).
          // `normalizePaymentRow` (gleich unten, via `normalizeOrderPayments`) setzt `due = ""`
          // UNBEDINGT, wenn es leer/fehlend ist - ohne diese Vorbelegung zeigte das Eingabefeld
          // "Fixes Zahlungsziel" ein gespeichertes Datum nie an, obwohl es existiert.
          if (!zeile.due && zeile.dueDate) {
            zeile.due = zeile.dueDate;
            // Fixwelle 2 der Gesamtpruefung (2026-09-01): `dueDate` blieb bisher NEBEN `due`
            // stehen - `ratenFelderAusZeile` (`due || dueDate`) fiel beim Leeren von `due` dann
            // lautlos auf das weiterhin vorhandene `dueDate` zurueck, und der Vergleich gegen die
            // ebenso rueckfallende "alte" Zeile sah ueberhaupt keine Aenderung: das Loeschen eines
            // geladenen Datums verpuffte, ohne dass ein PATCH je geplant wurde.
            delete zeile.dueDate;
          }
          return zeile;
        })
      : rohe,
  } as Order;
  // Seit Nachtrag 01.10.2026 (Plan 2026-10-02, E8): nur die vorhandenen Raten normalisieren, keine
  // Split-Standardraten und keine Phantomrate "Gesamtzahlung 100" mehr (`ratenFuerAnzeige`). Eine
  // Bestellung ohne Raten zeigt "Kein Zahlungsplan". `paid` bleibt das rohe Kopf-Flag: es ist die
  // verbindliche Quelle fuer "Bestellung bezahlt".
  kopie.payments = ratenFuerAnzeige(Array.isArray(kopie.payments) ? (kopie.payments as OrderPayment[]) : []);
  kopie.paid = rohesPaid as boolean;
  return kopie;
}

export interface ZahlungTabProps {
  bestellung: Order;
  /**
   * `DB.suppliers` - reine Durchreichung: `VorlagenAuswahl` gibt sie an `Ratenplan` weiter, wo sie laut dortigem
   * Kommentar ungenutzt ist (Betraege nur mit `fmtUsd`). Seit Ticket H liest `ZahlungTab` sie nicht mehr selbst
   * (frueher `orderCurrency` in der Kurssicherung und im Mail-Entwurf). Entfernen waere ein Umbau durch
   * `VorlagenAuswahl`, `Ratenplan` und deren Tests und gehoert nicht in dieses Ticket.
   */
  lieferanten: readonly Supplier[];
  /** Die fuer den Lieferanten der Bestellung freigegebenen Zahlungsziele. */
  freigaben: readonly FreigabeZeile[];
  /**
   * Der Vorlagenkatalog (mit `rows`) - die Oberflaeche prueft damit, ob eine Freigabe verwendbar ist
   * (`lib/zahlungsplanEditor.ts#vorlageVerwendbar`) und nennt sonst den Grund. Fehlt eine Vorlage hier,
   * gilt sie als verwendbar; der Server entscheidet dann.
   */
  vorlagen?: readonly PaymentTemplateRow[];
  /** `activeResponsibilityModules()` (index.html:8834) - fuer `ensurePaymentRequestTask`. */
  module: readonly ResponsibilityModule[];
  /** `CURRENT_USER` - gelesen werden `displayName`/`username` (`index.html:9885`). */
  nutzer?: { displayName?: unknown; username?: unknown } | null;
  /** Der Rechtesatz des Kontos - hier TATSAECHLICH gelesen, siehe Kopfkommentar „Rechte". */
  rechte: ReadonlySet<string>;
  /** Nach einem unbestaetigten Zahlungsziel-Snapshot bleiben Zahlvorgaenge bis zum Reload gesperrt. */
  schreibgeschuetzt?: boolean;
  onAendern: (id: string, aenderung: Record<string, unknown>) => void;
  /**
   * Setzt das Zahlungsziel samt Snapshot und Raten ueber den eigenen serverseitigen Schreibweg. `raten` (Ticket G)
   * kommt von „Zurück auf Vorlage“ (`ersetzen`); ohne Angabe entscheidet der Server (`automatisch`). Der Rueckruf
   * wird unveraendert an `VorlagenAuswahl` durchgereicht.
   */
  onZahlungszielOption: (templateId: string, raten?: ZahlungszielRaten) => void;
  /** "Individuell anpassen": setzt die Herkunft auf `individuell` (`POST .../payment-plan/individualize`). */
  onPlanIndividualisieren: (id: string) => void;
  /** "Speichern" im individuellen Editor: der ganze Plan atomar (`PUT .../payment-plan`). */
  onPlanSpeichern: (id: string, raten: PlanRateEingabe[]) => void;
  /**
   * Gleicht den alten gegen den neuen Ratensatz ab und fuehrt die noetigen POST/PATCH/DELETE-
   * Aufrufe gegen `/api/orders/:id/payments[...]` aus (`App.tsx#drawerRatenSynchronisieren`).
   * ERSETZT den fruehren, ungueltigen Weg ueber `onAendern(id, { payments, paymentMode, ... })`
   * (RIEGEL 2, `api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG`).
   */
  onRatenSynchronisieren: (id: string, alte: OrderPayment[], neue: OrderPayment[]) => void;
  /**
   * `zahlungAnstossen` unten baut BEIDES hier: `aenderung` (`paymentInitiatedAt`/
   * `paymentInitiatedBy`, Feldgruppe `payment-request`) und `aufgabe` (Titel/Beschreibung/
   * Faelligkeit aus `ensurePaymentRequestTask`, auf einer lokalen Arbeitskopie - die Bestellung
   * selbst bleibt unveraendert) und uebergibt BEIDE in EINEM Aufruf.
   *
   * EIN Prop statt zwei (Nachbesserung zu e2b8fbfb, Asana 1218171668075282): `onAendern` (Extras)
   * und ein fruehers, eigenes `onZahlungsauftrag` liefen bis hierher als ZWEI unabhaengige Ketten -
   * der Knopf blieb waehrend des Schreibens bedienbar, und beide Ketten luden am Ende unabhaengig
   * nach (`state/reduzierer.ts:497`, ersetzt den GANZEN Datensatz): antwortete die Extras-Kette
   * spaeter als die Aufgaben-Kette, fehlte der eben angelegte Zahlungsauftrag wieder im Speicher,
   * und der naechste Klick legte einen zweiten an. `App.tsx#drawerZahlungAnstossen` sequenziert die
   * beiden Schreibwege jetzt HINTEREINANDER (Aufgabenteil erst NACH der Extras-Antwort) und sperrt
   * die Bestellung fuer die Dauer der ganzen Kette - siehe dort.
   */
  onZahlungAnstossen: (
    id: string,
    aenderung: Record<string, unknown>,
    aufgabe: { title: string; description: string; due: string },
  ) => void;
  onZahlstatusZuruecksetzen?: (bestellungId: string) => void;
  /**
   * Ist-Zahlung je Rate (Bezahlt markieren, Datum, Zuruecknehmen, Belege). Ohne dieses Prop oder ohne das
   * Recht `manage_payments` zeigen die Ratenkarten die Ist-Zahlung nur an.
   */
  istZahlung?: IstZahlungHandler;
}

export function ZahlungTab({
  bestellung,
  lieferanten,
  freigaben,
  vorlagen,
  module,
  nutzer,
  rechte,
  schreibgeschuetzt,
  onAendern,
  onZahlungszielOption,
  onPlanIndividualisieren,
  onPlanSpeichern,
  onRatenSynchronisieren,
  onZahlungAnstossen,
  onZahlstatusZuruecksetzen,
  istZahlung,
}: ZahlungTabProps) {
  const id = bestellung.id as string;
  const istAktionen: IstZahlungAktionen | undefined =
    istZahlung && rechte.has("manage_payments")
      ? {
          bezahltMarkieren: (rate) => istZahlung.bezahltMarkieren(id, rate),
          datumAendern: (zahlung, paidOn) => istZahlung.datumAendern(id, zahlung, paidOn),
          zuruecknehmen: (rate, zahlung) => istZahlung.zuruecknehmen(id, rate, zahlung),
          belegHochladen: (zahlung, datei) => istZahlung.belegHochladen(id, zahlung, datei),
          belegVerknuepfen: (zahlung, documentId) => istZahlung.belegVerknuepfen(id, zahlung, documentId),
        }
      : undefined;
  const o = bestellungFuerAnzeige(bestellung);
  const nutzerName =
    (nutzer?.displayName as string) || (nutzer?.username as string) || "Unbekannt";

  /**
   * Eine frische Arbeitskopie fuer einen Handler - Raten und Belege je Eintrag kopiert, damit die
   * an Ort und Stelle arbeitenden `lib/`-Funktionen (`normalizePaymentRow`) die Requisiten nicht anfassen.
   *
   * `pdfs`/`docsManual`/`docsNa` werden nur kopiert, wenn sie VORHANDEN sind. Sie hier vorsorglich
   * anzulegen wuerde die Zusicherung von Befund 10 (`zahlungTab.test.tsx`: weder `docsManual` noch `docsNa` erreichen
   * den Server) wertlos machen. Der Zahlungsart-Wechsel, der im Original (`index.html:4932`) ueber
   * `syncPaymentDocumentTypes` die Belegtypen nachfuehrte, ist entfallen (E8); die Funktion ist mit dem Anzahlungs- und dem
   * Restzahlungs-/Gesamtzahlungsbeleg auch aus `lib/payments.ts` verschwunden.
   */
  const arbeitskopie = (): Order =>
    ({
      ...o,
      payments: ((o.payments as OrderPayment[] | undefined) || []).map((p) => ({ ...p })),
      ...(o.pdfs ? { pdfs: (o.pdfs as OrderDocument[]).map((d) => ({ ...d })) } : {}),
      ...(o.docsManual ? { docsManual: { ...(o.docsManual as Record<string, unknown>) } } : {}),
      ...(o.docsNa ? { docsNa: { ...(o.docsNa as Record<string, unknown>) } } : {}),
    }) as Order;

  /**
   * `initiatePayment()` (`index.html:9880`) - ohne die Rechtepruefung, die `Freigabe` traegt.
   *
   * ERSETZT seit dem Fix zu Asana 1218171668075282 den frueheren Weg ueber ein einziges
   * `onAendern(id, { paymentInitiatedAt, paymentInitiatedBy, tasks })`: `tasks` fuehrt KEINE
   * Feldgruppe (`api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG`), RIEGEL 2 liess `bestellungAendern`
   * deshalb mit `FeldOhneGruppeFehler` scheitern, BEVOR ueberhaupt etwas gesendet wurde - der Klick
   * schlug bei JEDER Bestellung fehl. `ensurePaymentRequestTask` bleibt die einzige Stelle, die
   * Titel, Beschreibung und `due` des Zahlungsauftrags FORMT; sie arbeitet weiterhin auf einer
   * lokalen Arbeitskopie (die Bestellung darf nicht mutiert werden). Ihr Ergebnis geht nicht mehr
   * als `tasks` an `onAendern`, sondern in EINEM Aufruf ueber `onZahlungAnstossen` - siehe dessen
   * Kommentar an der Prop, "EIN Prop statt zwei", fuer den Grund (Nachbesserung zu e2b8fbfb).
   */
  const zahlungAnstossen = (): void => {
    const requestedAt = new Date().toISOString();
    const kopie = {
      ...o,
      tasks: ((o.tasks as OrderTask[] | undefined) || []).map((t) => ({ ...t })),
    } as Order;
    const task = ensurePaymentRequestTask(
      kopie,
      requestedAt,
      nutzerName,
      module as ResponsibilityModule[],
    );
    onZahlungAnstossen(
      id,
      { paymentInitiatedAt: requestedAt, paymentInitiatedBy: nutzerName },
      {
        title: task.name as string,
        description: task.description as string,
        due: task.due as string,
      },
    );
  };

  const zahlungAnstossenZuruecksetzen = (): void => {
    onAendern(id, { paymentInitiatedAt: null, paymentInitiatedBy: null });
  };

  // `setPaymentMode` (`index.html:9421`) und `applyTermsToPayments` (`:9511`) entfallen als Bedienung:
  // Der Umschalter "Zahlungsart festlegen" ist durch die drei Zustaende ersetzt (Nachtrag 01.10.2026,
  // `VorlagenAuswahl.tsx`); "In Raten umwandeln" fuellt dort nur noch den Editor vor (E9).

  /**
   * `patchPayment(i, key, value)` (`index.html:9441-9468`).
   *
   * ERSETZT seit Task 7 (Zahlungsraten-Reparatur) den fruehren Weg ueber ein einziges
   * `onAendern(id, { payments, paid })` - derselbe erfolglose Weg, aus demselben Grund, wie
   * `zahlungsartSetzen`/`inRatenUmwandeln` vor Task 5: `payments` fuehrt KEINE Feldgruppe
   * (`api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG`), RIEGEL 2 liess `bestellungAendern` deshalb mit
   * `FeldOhneGruppeFehler` scheitern, BEVOR ueberhaupt etwas gesendet wurde: die Aenderung einer
   * einzelnen Rate blieb ohne dauerhafte Wirkung. `onRatenSynchronisieren` (`App.tsx`) fuehrt den
   * neuen Ratensatz jetzt ueber die echten Endpunkte (`POST`/`PATCH`/`DELETE .../payments[...]`).
   */
  const rateSetzen = (index: number, feld: keyof OrderPayment, wert: string): void => {
    const alteRaten = ((o.payments as OrderPayment[]) || []).map((p) => ({ ...p }));
    const kopie = arbeitskopie();
    const p = ((kopie.payments as OrderPayment[]) || [])[index];
    if (!p) return;
    if (feld === "status") {
      // Seit der Ist-Zahlung (Plan 2026-10-04) setzt der Zahlstatus nie mehr `paid` und nimmt es nie zurueck:
      // beides laeuft ueber `istZahlung` (Bezahlt markieren / Zahlung zuruecknehmen), der Raten-PATCH wuerde 409.
      if (wert === "paid" || paymentStatusOf(p) === "paid") return;
      p.status = PAYMENT_STATUSES[wert] ? wert : "open";
      p.paid = false;
    } else {
      (p as Record<string, unknown>)[feld as string] = wert;
      // R4 (Auftrag 2026-10-01): ein von Hand eingetragenes Datum macht aus der Ankerrate ein
      // Handdatum - der Anker springt sichtbar auf "Handdatum" und wird mitgeschrieben, damit kein
      // spaeterer Nachzug die Eingabe ueberschreibt. Ein GELEERTES Datum laesst den Anker stehen.
      if (feld === "due" && wert !== "" && p.anchor) p.anchor = null;
    }
    // Der Kopf-Merker `paid` wird hier NICHT mehr aus den Raten neu berechnet und mitgeschickt - das
    // waere wieder ein Aufruf, der `payments` und `paid` mischt (RIEGEL 2). Eine einzelne Rate auf
    // "bezahlt" zu setzen aendert den Gesamtstatus der Bestellung bewusst nicht automatisch mit.
    onRatenSynchronisieren(id, alteRaten, kopie.payments as OrderPayment[]);
  };

  // Plan 2026-10-04 (Ist-Zahlung): Belege haengen an der bezahlten Rate (`payments[].actual.documents`). Die
  // Zahlen nennt die Rueckfrage des Zahlstatus-Resets, damit niemand unbemerkt Ist-Zahlungen verliert.
  const raten = (Array.isArray(bestellung.payments) ? bestellung.payments : []) as OrderPayment[];
  const istZahlungen = raten.map((p) => p.actual).filter((a): a is IstZahlungTyp => !!a);
  const istBelegAnzahl = istZahlungen.reduce((summe, a) => summe + a.documents.length, 0);
  const hatZahlstatus =
    !!bestellung.paymentTerms ||
    !!bestellung.paymentTermsTemplateId ||
    !!(bestellung as { paymentPlanSource?: unknown }).paymentPlanSource ||
    ((bestellung.payments as unknown[] | undefined) ?? []).length > 0 ||
    !!bestellung.paid ||
    !!bestellung.paidDate;

  return (
    <>
      <Freigabe
        bestellung={o}
        rechte={rechte}
        onAnstossen={zahlungAnstossen}
        onZuruecksetzen={zahlungAnstossenZuruecksetzen}
      />

      <VorlagenAuswahl
        key={id}
        bestellung={o}
        lieferanten={lieferanten}
        freigaben={freigaben}
        vorlagen={vorlagen}
        schreibgeschuetzt={schreibgeschuetzt}
        planRecht={rechte.has("manage_payments")}
        onZahlungszielOption={onZahlungszielOption}
        onPlanIndividualisieren={() => onPlanIndividualisieren(id)}
        onPlanSpeichern={(raten) => onPlanSpeichern(id, raten)}
        onRate={rateSetzen}
        istAktionen={istAktionen}
      />

      {hatZahlstatus && onZahlstatusZuruecksetzen ? (
        <button
          type="button"
          className="btn-ghost"
          data-permission="manage_payments"
          disabled={schreibgeschuetzt}
          onClick={() => {
            if (!rechte.has("manage_payments") || schreibgeschuetzt) return;
            // Ruling Task 2: der Reset loescht Ist-Zahlungen samt Belegverknuepfungen (CASCADE); die
            // Dokumente selbst bleiben. Der Zusatz erscheint nur, wenn es etwas zu verlieren gibt.
            const frage =
              "Zahlstatus und Raten wirklich zurücksetzen?" +
              (istZahlungen.length > 0
                ? `\n\nDabei werden ${istZahlungen.length} erfasste Zahlung(en) und ${istBelegAnzahl} Belegverknüpfung(en) entfernt; die Belege selbst bleiben gespeichert und können erneut verknüpft werden.`
                : "");
            if (confirm(frage)) {
              onZahlstatusZuruecksetzen(id);
            }
          }}
        >
          Zahlstatus zurücksetzen
        </button>
      ) : null}

      <GeloesteBelege bestellung={bestellung} />
    </>
  );
}
